import { DatePickerHeader } from '#PRIVATE_DatePickerHeader';
import { MonthView } from '#PRIVATE_MonthView';
import { MonthsGridView, YearsGridView } from '#PRIVATE_MonthYearGridView';
import { Flex } from '#UNSAFE_Flex';
import { useTestId, type TestIdProps } from '#hooks/UNSAFE_useTestId';
import { type CalendarDateRequired } from '#utils/UNSAFE_calendarDateUtils';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { useDatePicker } from './useDatePicker';
import { useBreakpointValues } from '#hooks/UNSAFE_useBreakpointValues';
import { TodayButton } from './TodayButton';
import { datePickerPropVars } from './themes/DatePickerContract.css';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { DatePickerRedwoodTheme } from './themes/redwood/DatePickerTheme';
import { Size } from '#utils/UNSAFE_size';

export type DayFormatOptions = {
  state: 'enabled' | 'disabled' | 'restricted';
};

type Props = TestIdProps & {
  /**
   * A function that receives a date and returns an object specifying how to format the provided date.
   * This function is called for each date that is rendered and this can be used to control how individual
   * dates are rendered - disabled, restricted, or enabled.
   * @param date The date to format
   * @returns The format options
   */
  dayFormatter?: (date: CalendarDateRequired) => DayFormatOptions;

  /**
   * The daysOutsideMonth property affects the day cells that are outside of the current month.
   * If set to 'hidden', then the days outside of the current month will be hidden.
   * If set to 'selectable', then the days outside of the current month will be visible and selectable.
   * This defaults to 'hidden'.
   */
  daysOutsideMonth?: 'hidden' | 'selectable';

  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;

  /**
   * The monthAndYearPicker property can be used to specify whether or not the DatePicker should show the
   * Months Grid View and Years Grid View.
   */
  monthAndYearPicker?: 'on' | 'off';

  /**
   * The maximum selectable date. The max must be equal or greater than the min if they are both specified or an error is thrown.
   */
  max?: CalendarDateRequired;

  /**
   * Specifies the component style maxWidth.
   * <p>If not specified, defaults to the theme specified maxWidth.<br/>
   * This attribute accepts values of type:<br/>
   * 0 | `${number}${CssUnits}` | `${number}x` | `--${string}` | `var($string})` | `${number}%`</p>
   * <p>When this component is used in composition and the component should take up 100% of the parent container's
   * width, set maxWidth to '100%'.<br/>
   * Because there is a theme default value for maxWidth, when setting the width property to achieve a fixed width,
   * set the maxWidth property to the same value.</p>
   */
  maxWidth?: Size;

  /**
   * The minimum selectable date. The min must be equal or less than the max if they are both specified or an error is thrown.
   */
  min?: CalendarDateRequired;

  /**
   * The todayTimeZone property is used to compute what the highlighted today cell is for the DatePicker.
   * This defaults to the user's system timezone.
   * <p>
   * The only reason an application would set the todayTimeZone property is if they want the
   * highlighted today cell to be in a different timezone than the user's system's timezone,
   * like if the user has a preferred timezone that is not where they are physically working.
   * </p>
   */
  todayTimeZone?: Intl.DateTimeFormatOptions['timeZone'];

  /**
   * Specifies how to render the 'Go to today' button.
   * <ul>
   * <li>'visible' - set this property to 'visible' to show the 'Go to today' button.</li>
   * <li>'hidden' - set this property to 'hidden' to hide the 'Go to today' button.</li>
   * </ul>
   */
  todayButton?: 'visible' | 'hidden';

  /**
   * The value of the DatePicker. The value property is the date that looks selected in the DatePicker.
   * If the value is undefined, no day will look selected.
   * <p>
   * The DatePicker initially opens to the selected date if there is one and it is within the min/max range.
   * If there is no value, the DatePicker initially opens to today's month.
   * </p>
   */
  value?: CalendarDateRequired;

  /**
   * Specifies the component style width.
   * <p>If not specified, defaults to '100%'.</p>
   * <p>This attribute accepts values of type:</p>
   * <p>0 | `${number}${CssUnits}` | `${number}x` | `--${string}`  | `var($string})` | `${number}%`</p>
   * <br/>
   * <p>Because there is a theme default value for maxWidth, when setting the width property to achieve a fixed width,
   * set the maxWidth property to the same value.</p>
   */
  width?: Size;

  /**
   * Callback invoked when the user selects a date in the DatePicker. This callback is called even when the previous
   * value and the value are the same, that is, when the user selects an already selected date.
   * The app needs to handle this to write back the controlled 'value' property, or else no date will be shown
   * to be selected.
   * If the app is using a DatePicker to control another component, like as a way to navigate an Event Calendar,
   * then they would listen to the onCommit to get the value of the selected date.
   */
  onCommit: (detail: ValueUpdateDetail<CalendarDateRequired>) => void;
};

/**
 * A DatePicker is a calendar interface that allows users to select a single date.
 */
export const DatePicker = ({
  dayFormatter,
  daysOutsideMonth,
  isReadonly = false,
  monthAndYearPicker = 'on',
  max,
  maxWidth,
  min,
  value,
  testId,
  todayTimeZone,
  todayButton = 'visible',
  width,
  onCommit
}: Props) => {
  const { accessibleLabels, handlers, state, todaysDate, toggleSelection, viewContainerProps } =
    useDatePicker({
      isReadonly,
      max,
      min,
      todayTimeZone,
      value,
      onCommit
    });
  const { month, year } = state.focusableDate;
  const isMonthYearPickerEnabled = monthAndYearPicker === 'on' && !isReadonly;
  // When the screen is < 360px, we hide the DatePickerHeader's today button and show a Today button
  // on the bottom of the DatePicker instead.
  // NOTE: I cannot use xs, sm here since useBreakpointValues's types do not allow it.
  const shouldShowTodayFooterButton = useBreakpointValues(
    {
      sm: true,
      md: false
    },
    { sm: '0px', md: '360px' }
  );

  const showTodayFooterButton =
    !state.isTodayNavigationButtonHidden &&
    shouldShowTodayFooterButton &&
    todayButton === 'visible';

  // we need to render the baseTheme on the root dom element so that the CSS vars in datePickerPropVars will be defined.
  const { baseTheme } = useComponentTheme(DatePickerRedwoodTheme);

  const testIdProps = useTestId(testId);

  return (
    <div class={baseTheme} {...testIdProps}>
      <Flex
        direction="column"
        gap={
          state.currentView === 'monthView'
            ? datePickerPropVars.calendarGridRowGap
            : datePickerPropVars.monthYearGridRowGap
        }
        height={
          showTodayFooterButton
            ? datePickerPropVars.todayFooterButtonHeight
            : datePickerPropVars.height
        }
        width={width !== undefined ? width : datePickerPropVars.width}
        maxWidth={maxWidth !== undefined ? maxWidth : datePickerPropVars.maxWidth}>
        <DatePickerHeader
          testId={testId ? testId + '_header' : undefined}
          month={month}
          year={year}
          isPreviousNavigationButtonHidden={state.isPreviousNavigationButtonHidden}
          isTodayNavigationButtonHidden={
            todayButton === 'hidden' ||
            state.isTodayNavigationButtonHidden ||
            shouldShowTodayFooterButton
          }
          isNextNavigationButtonHidden={state.isNextNavigationButtonHidden}
          monthYearHeader={isMonthYearPickerEnabled ? 'independentButtons' : 'text'}
          toggleSelection={isMonthYearPickerEnabled ? toggleSelection : undefined}
          previousNavigationAccessibleLabel={accessibleLabels.previous}
          todayNavigationAccessibleLabel={accessibleLabels.goToToday}
          nextNavigationAccessibleLabel={accessibleLabels.next}
          onTodayNavigationAction={handlers.handleTodayNavigation}
          onPreviousNavigationAction={handlers.handlePreviousNavigation}
          onNextNavigationAction={handlers.handleNextNavigation}
          onMonthToggle={isMonthYearPickerEnabled ? handlers.handleMonthToggle : undefined}
          onYearToggle={isMonthYearPickerEnabled ? handlers.handleYearToggle : undefined}
        />
        <div {...viewContainerProps}>
          {state.currentView === 'monthView' && (
            <MonthView
              dayFormatter={dayFormatter}
              daysOutsideMonth={daysOutsideMonth}
              isReadonly={isReadonly}
              max={max}
              min={min}
              selectedDate={value}
              focusableDate={state.focusableDate}
              isFocused={state.isViewFocused}
              testId={testId ? testId + '_monthView' : undefined}
              todaysDate={todaysDate}
              onNavigation={handlers.handleMonthViewNavigation}
              onAction={handlers.handleMonthViewAction}
            />
          )}
          {state.currentView === 'monthsGridView' && (
            <MonthsGridView
              currentMonth={todaysDate.month}
              focusableMonth={state.focusableMonth}
              isFocused={state.isViewFocused}
              selectedMonth={month}
              testId={testId ? testId + '_monthsGridView' : undefined}
              onAction={handlers.handleMonthsGridViewAction}
              onNavigation={handlers.handleMonthsGridViewNavigation}
            />
          )}
          {state.currentView === 'yearsGridView' && (
            <YearsGridView
              currentYear={todaysDate.year}
              focusableYear={state.focusableYear}
              isFocused={state.isViewFocused}
              selectedYear={year}
              testId={testId ? testId + '_yearsGridView' : undefined}
              onAction={handlers.handleYearsGridViewAction}
              onNavigation={handlers.handleYearsGridViewNavigation}
            />
          )}
        </div>
        {showTodayFooterButton && (
          <div>
            <TodayButton
              testId={testId ? testId + '_footerTodayButton' : undefined}
              todayNavigationLabel={accessibleLabels.today}
              todayNavigationAriaLabel={accessibleLabels.goToToday}
              onTodayNavigationAction={handlers.handleTodayNavigation}
            />
          </div>
        )}
      </Flex>
    </div>
  );
};
